/**
 * Импорт фото платья из CRM в «Наши фото» (спека 2026-10-22-crm-photo-import-design.md).
 *
 * Без `modelId` — поиск (поле «Серия» — серия, поле «Модель» — номер модели (артикул) или её id) → кандидаты → модель. С `modelId` (и `dressId`
 * у «Доимпорта») поиск пропускается. Модель: шапка, новые по трём группам, свёрнутые «Исключены раньше»,
 * «Не скачались», «Уже импортированы»; кнопка «Импортировать N, исключить M».
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Check } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router";

import { btn, btnGhost, btnPrimary, ErrorLine, input } from "@/components/trends/ui";
import { ApiFailure } from "@/lib/api";
import { GROUPS, importBody, initialChecked, sections, setMany, type CrmPhoto } from "@/lib/crmImport";
import { thumbUrl } from "@/lib/media";
import {
  loadCrmModel,
  lookupCrm,
  photoKeys,
  runCrmImport,
  type CrmCandidate,
  type CrmImportResult,
  type CrmModelData,
} from "@/lib/photos";
import { retryQuery } from "@/lib/trends";
import { Dialog } from "@/shared/ui/Dialog";

const KIND: Record<CrmPhoto["kind"], string> = {
  main: "главное",
  mp: "маркетплейс",
  front: "вид спереди",
  other: "прочее",
};

export function CrmImportDialog({
  open,
  onClose,
  modelId,
  dressId,
}: {
  open: boolean;
  onClose: () => void;
  modelId?: number | undefined;
  dressId?: number | undefined;
}) {
  const [picked, setPicked] = useState<number | null>(null);
  const id = modelId ?? picked;
  // Окно остаётся смонтированным: закрыли — следующий вход снова с поиска.
  useEffect(() => {
    if (!open) setPicked(null);
  }, [open]);
  return (
    <Dialog open={open} title="Импорт фото из CRM" onClose={onClose} wide>
      {open ? (
        id === null ? <Search onPick={setPicked} /> : <Model
            key={id}
            modelId={id}
            dressId={dressId}
            onBack={modelId === undefined ? () => setPicked(null) : undefined}
          />
      ) : null}
    </Dialog>
  );
}

function Search({ onPick }: { onPick: (id: number) => void }) {
  const [series, setSeries] = useState("");
  const [model, setModel] = useState("");
  // Что искали: пустой ответ называет это в подписи.
  const [asked, setAsked] = useState<{ kind: "Серия" | "Модель"; value: string } | null>(null);
  const find = useMutation({
    mutationFn: () => lookupCrm(series.trim() ? { series: series.trim() } : { model: model.trim() }),
    onSuccess: (got) => {
      if (got.candidates.length === 1) onPick(got.candidates[0]!.model_id);
    },
  });
  const found: CrmCandidate[] | undefined = find.data?.candidates;
  const filled = Boolean(series.trim() || model.trim());
  return (
    <div className="grid gap-3 p-4">
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (find.isPending || !filled) return;
          setAsked(series.trim() ? { kind: "Серия", value: series.trim() } : { kind: "Модель", value: model.trim() });
          find.mutate();
        }}
      >
        <input
          aria-label="Серия"
          className={clsx(input, "w-60")}
          placeholder="Серия"
          autoFocus
          value={series}
          onChange={(e) => {
            setSeries(e.target.value);
            setModel("");
          }}
        />
        <input
          aria-label="Модель"
          className={clsx(input, "w-40")}
          placeholder="Модель"
          value={model}
          onChange={(e) => {
            setModel(e.target.value);
            setSeries("");
          }}
        />
        <button type="submit" className={btn} disabled={find.isPending || !filled}>
          {find.isPending ? "Ищу…" : "Найти"}
        </button>
      </form>
      <ErrorLine error={find.error} />
      {found && found.length === 0 ? <span className="text-[12.5px] text-warn">
          {asked ? `${asked.kind} ${asked.value} в CRM не найдена` : "В CRM не найдено"}
        </span> : null}
      {found && found.length > 1 ? (
        <div className="flex flex-wrap gap-1.5">
          {found.map((c) => (
            <button key={c.model_id} type="button" className={btn} onClick={() => onPick(c.model_id)}>
              Модель {c.name ?? c.series ?? "—"} · #{c.model_id} ·{" "}
              {c.in_stock > 0 ? `в наличии ${c.in_stock}` : "нет в наличии"}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function Model({
  modelId,
  dressId,
  onBack,
}: {
  modelId: number;
  dressId?: number | undefined;
  onBack?: (() => void) | undefined;
}) {
  const q = useQuery({
    queryKey: photoKeys.crmImport(modelId),
    queryFn: () => loadCrmModel(modelId),
    retry: retryQuery,
  });
  if (q.isPending) return <div className="p-4 text-[12.5px] text-fg-faint">Загружаю фото из CRM…</div>;
  if (q.isError)
    return (
      <div className="p-4">
        <ErrorLine error={q.error} />
      </div>
    );
  return <ModelBody data={q.data} modelId={modelId} dressId={dressId} onBack={onBack} />;
}

function ModelBody({
  data,
  modelId,
  dressId,
  onBack,
}: {
  data: CrmModelData;
  modelId: number;
  dressId?: number | undefined;
  onBack?: (() => void) | undefined;
}) {
  const client = useQueryClient();
  const { model, folders, photos, score_error } = data;
  const [checked, setChecked] = useState<Set<number>>(() => initialChecked(photos));
  // Перезагрузка модели после импорта отдаёт новый массив: выбор считаем заново.
  useEffect(() => setChecked(initialChecked(photos)), [photos]);
  const [target, setTarget] = useState<number | null>(dressId ?? (folders.length === 1 ? (folders[0]?.id ?? null) : null));
  const [needChoice, setNeedChoice] = useState(false);
  const [result, setResult] = useState<CrmImportResult | null>(null);

  const sec = sections(photos);
  const body = importBody(photos, checked, target);
  const nImport = body.import_ids.length;
  const nExclude = body.exclude_ids.length;
  const freshAll = GROUPS.flatMap((g) => sec.fresh[g.key]);
  const choose = (folders.length > 1 && dressId === undefined) || needChoice;
  const noTarget = choose && target === null;

  const run = useMutation({
    mutationFn: () => runCrmImport(modelId, body),
    onSuccess: (got) => {
      setResult(got);
      void client.invalidateQueries({ queryKey: photoKeys.list });
      if (got.dress_id !== null) void client.invalidateQueries({ queryKey: photoKeys.dress(got.dress_id) });
      void client.invalidateQueries({ queryKey: photoKeys.crmImport(modelId) });
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.code === "CHOOSE_DRESS") setNeedChoice(true);
    },
  });
  const chooseErr = run.error instanceof ApiFailure && run.error.code === "CHOOSE_DRESS";

  const toggle = (id: number) => setChecked((c) => setMany(c, [id], !c.has(id)));
  const folderName = (f: { id: number; name: string | null }) => f.name ?? `Платье #${f.id}`;
  const where =
    dressId !== undefined ? (
      <span>папка «{folderName(folders.find((f) => f.id === dressId) ?? { id: dressId, name: null })}»</span>
    ) : choose ? (
      <select
        aria-label="Куда"
        className={clsx(input, "w-64")}
        value={target ?? ""}
        onChange={(e) => setTarget(e.target.value ? Number(e.target.value) : null)}
      >
        <option value="">— выберите папку —</option>
        {folders.map((f) => (
          <option key={f.id} value={f.id}>
            {folderName(f)} · {f.count} фото
          </option>
        ))}
      </select>
    ) : folders.length === 1 ? (
      <span>папка «{folderName(folders[0]!)}»</span>
    ) : (
      <span>новая папка</span>
    );

  const tile = (ph: CrmPhoto, withBox: boolean) => {
    const on = checked.has(ph.media_id);
    const src = ph.state === "imported" && ph.thumb_path ? thumbUrl(ph.thumb_path) : ph.url;
    const caption = `${ph.score === null ? "нет оценки" : ph.score.toFixed(2)} · ${KIND[ph.kind]}`;
    const inner = (
      <>
        <div className="relative aspect-[3/4] overflow-hidden bg-bg-elevated">
          <img src={src} loading="lazy" alt="" className="absolute inset-0 h-full w-full object-cover" />
          {withBox ? (
            <span
              className={clsx(
                "absolute left-1.5 top-1.5 grid h-5 w-5 place-items-center rounded border",
                on ? "border-accent bg-accent text-white" : "border-border-strong bg-bg/80",
              )}
            >
              {on ? <Check size={13} /> : null}
            </span>
          ) : null}
        </div>
        <div className="px-2 py-1 text-[11.5px] text-fg-muted tnum">{caption}</div>
        {ph.state === "failed" && ph.error ? <div className="px-2 pb-1 text-[11px] text-warn">{ph.error}</div> : null}
      </>
    );
    const cls = clsx(
      "grid content-start overflow-hidden rounded-[var(--radius-card)] border bg-bg-subtle text-left",
      on ? "border-accent" : "border-border",
    );
    return withBox ? (
      <button key={ph.media_id} type="button" className={cls} aria-pressed={on} onClick={() => toggle(ph.media_id)}>
        {inner}
      </button>
    ) : (
      <div key={ph.media_id} className={cls}>
        {inner}
      </div>
    );
  };

  const grid = (list: CrmPhoto[], withBox = true): ReactNode => (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-2">{list.map((p) => tile(p, withBox))}</div>
  );
  const bulk = (list: CrmPhoto[]) => {
    const ids = list.map((p) => p.media_id);
    return (
      <span className="flex gap-1">
        <button type="button" className={btnGhost} onClick={() => setChecked((c) => setMany(c, ids, true))}>
          Выделить все
        </button>
        <button type="button" className={btnGhost} onClick={() => setChecked((c) => setMany(c, ids, false))}>
          Снять
        </button>
      </span>
    );
  };
  const folded = (title: string, list: CrmPhoto[], withBox: boolean, extra?: ReactNode) =>
    list.length ? (
      <details className="grid gap-2" data-annot={title}>
        <summary className="cursor-pointer text-[13px] font-medium text-fg">
          {title} ({list.length})
        </summary>
        <div className="mt-2 grid gap-2">
          {extra}
          {grid(list, withBox)}
        </div>
      </details>
    ) : null;

  return (
    <div className="grid gap-4 p-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-fg">
        {onBack ? (
          <button type="button" className={btnGhost} onClick={onBack}>
            ← другая модель
          </button>
        ) : null}
        <span className="font-medium">
          Модель {model.name ?? model.series ?? "—"} · #{model.id}
        </span>
        <span className={model.in_stock > 0 ? "text-fg-muted" : "text-warn"}>
          {model.in_stock > 0 ? `в наличии ${model.in_stock}` : "нет в наличии"}
        </span>
        {model.excluded ? <span className="text-warn">модель исключена из импорта топа</span> : null}
        <span className="flex items-center gap-1.5">Куда: {where}</span>
      </div>
      {score_error ? (
        <div className="rounded-[var(--radius-ui)] bg-warn-bg px-2 py-1 text-[12px] text-warn">
          Оценки «человек в кадре» недоступны: {score_error}
        </div>
      ) : null}

      {freshAll.length === 0 ? (
        <div className="text-[12.5px] text-fg-muted">Новых фото в CRM нет</div>
      ) : (
        <div className="grid gap-4">
          <div className="flex items-center gap-2 text-[13px] font-medium text-fg">
            Новые ({freshAll.length}) {bulk(freshAll)}
          </div>
          {GROUPS.map((g) =>
            sec.fresh[g.key].length ? (
              <section key={g.key} className="grid gap-2" data-annot={g.title}>
                <div className="flex flex-wrap items-center gap-2 text-[12.5px] text-fg">
                  <span className="font-medium">
                    {g.title} ({sec.fresh[g.key].length})
                  </span>
                  <span className="text-fg-faint">{g.hint}</span>
                  {bulk(sec.fresh[g.key])}
                </div>
                {grid(sec.fresh[g.key])}
              </section>
            ) : null,
          )}
        </div>
      )}

      {folded("Исключены раньше", sec.excluded, true)}
      {folded("Не скачались", sec.failed, true, bulk(sec.failed))}
      {folded("Уже импортированы", sec.imported, false)}

      <div className="sticky bottom-0 -mx-4 -mb-4 grid gap-2 border-t border-border bg-bg px-4 py-3">
        <ErrorLine error={chooseErr ? null : run.error} />
        {chooseErr || (needChoice && target === null) ? (
          <span className="text-[12.5px] text-warn">У модели несколько папок — выберите, куда класть фото.</span>
        ) : null}
        {result ? (
          <div className="flex flex-wrap items-center gap-3 text-[13px] text-fg">
            <span>
              Импортировано {result.imported} · исключено {result.excluded} · не скачалось {result.failed.length}
            </span>
            {result.dress_id !== null ? (
              <Link to={`/photos/${result.dress_id}`} className="text-accent-text hover:underline">
                Открыть папку
              </Link>
            ) : null}
          </div>
        ) : null}
        <div className="flex justify-end">
          <button
            type="button"
            className={btnPrimary}
            disabled={run.isPending || (nImport === 0 && nExclude === 0) || noTarget}
            onClick={() => run.mutate()}
          >
            {run.isPending ? "Импортирую…" : `Импортировать ${nImport}, исключить ${nExclude}`}
          </button>
        </div>
      </div>
    </div>
  );
}
